import {
  AccountSelectContainer,
  ConsultLabel,
  ConsultLabelMobile,
  ConsultLabelWarning,
  ConsultMetadataContainer,
  ContainerData,
  DetailsContainer,
  DivSearching,
  DownloadPdf,
  FormContainer,
  InfoText,
  Line,
  MobileContainer,
  NoCreditContainer,
  ProcessItem,
  ProcessList,
  ResponseContainer
} from "./styles";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { TAnalysisResults } from "../models";
import { toast } from "react-toastify";
import { HomeService } from "../services";
import { Input } from "../../../components/inputs";
import { Page } from "../../../components/page-base";
import theme from "../../../styles/theme";
import { TAccount, TAccountRes } from "../../accounts/models";
import { AccountsService } from "../../accounts/services";
import { useAuthContext, useNavigationContext } from "../../../contexts";
import { TSelectAccounts } from "../../scenarios/models";
import permissionsStore from "../../../store/permissions";
import { ScenariosService } from "../../scenarios/services";
import { useNavigate } from "react-router-dom";
import { OctagonAlert, AlertTriangle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  court,
  readFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage
} from "../utils/utils";
import socket from "@/config/socket";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useIsMobile } from "@/hooks";
import { cpf as cpfValidator } from "cpf-cnpj-validator";

import TermsModal from "@/modules/login/components/terms-modal";
import { FreeCredit } from "@/modules/credits/components/modals/free-credit";
import ContentPasteSearchOutlinedIcon from "@mui/icons-material/ContentPasteSearchOutlined";
import { EmbaixadorService } from "@/modules/embaixador/services";

// ===================
// Interface e Reducer para agrupar os estados
// ===================
interface ConsultationState {
  cpf: string;
  isDocValid: boolean;
  searching: boolean;
  progress: number;
  analysisResult: TAnalysisResults | null;
  invalidCpf: boolean;
  counter: number;
  selectedAccount: number;
  errorReturnConsult: boolean;
  accountsOptions: TSelectAccounts[];
  credits: number;
  displayStatus: boolean;
  noCredit: boolean;
  courtValue: number;
  fetch: boolean;
  canShow: boolean;
  loadingAnalysis: boolean;
  fileUrl: string;
  criterioDisplay: boolean;
  processesCount: number;
}

const initialState: ConsultationState = {
  cpf: "",
  isDocValid: false,
  searching: false,
  progress: 0,
  analysisResult: null,
  invalidCpf: false,
  errorReturnConsult: false,
  counter: 0,
  selectedAccount: 0,
  accountsOptions: [],
  credits: 0,
  displayStatus: false,
  noCredit: false,
  courtValue: 0,
  fetch: false,
  canShow: false,
  loadingAnalysis: false,
  criterioDisplay: false,
  fileUrl: "",
  processesCount: 0
};

type ConsultationAction =
  | { type: "SET_CPF"; payload: string }
  | { type: "SET_IS_DOC_VALID"; payload: boolean }
  | { type: "SET_SEARCHING"; payload: boolean }
  | { type: "SET_PROGRESS"; payload: number }
  | { type: "SET_ANALYSIS_RESULT"; payload: TAnalysisResults | null }
  | { type: "SET_INVALID_CPF"; payload: boolean }
  | { type: "SET_ERROR_RETURN_CONSULT"; payload: boolean }
  | { type: "SET_COUNTER"; payload: number }
  | { type: "INCREMENT_COUNTER" }
  | { type: "SET_SELECTED_ACCOUNT"; payload: number }
  | { type: "SET_ACCOUNTS_OPTIONS"; payload: TSelectAccounts[] }
  | { type: "SET_CREDITS"; payload: number }
  | { type: "SET_DISPLAY_STATUS"; payload: boolean }
  | { type: "SET_NO_CREDIT"; payload: boolean }
  | { type: "SET_COURT_VALUE"; payload: number }
  | { type: "SET_PROCESSES_COUNT"; payload: number }
  | { type: "SET_FETCH"; payload: boolean }
  | { type: "SET_CAN_SHOW"; payload: boolean }
  | { type: "SET_LOADING_ANALYSIS"; payload: boolean }
  | { type: "SET_DISPLAY_CRITERIO"; payload: boolean }
  | { type: "RESET" };

function consultationReducer(
  state: ConsultationState,
  action: ConsultationAction
): ConsultationState {
  switch (action.type) {
    case "SET_CPF":
      return { ...state, cpf: action.payload };
    case "SET_IS_DOC_VALID":
      return { ...state, isDocValid: action.payload };
    case "SET_SEARCHING":
      return { ...state, searching: action.payload };
    case "SET_PROGRESS":
      return { ...state, progress: action.payload };
    case "SET_ANALYSIS_RESULT":
      return { ...state, analysisResult: action.payload };
    case "SET_INVALID_CPF":
      return { ...state, invalidCpf: action.payload };
    case "SET_COUNTER":
      return { ...state, counter: action.payload };
    case "INCREMENT_COUNTER":
      return { ...state, counter: state.counter + 1 };
    case "SET_SELECTED_ACCOUNT":
      return { ...state, selectedAccount: action.payload };
    case "SET_ERROR_RETURN_CONSULT":
      return { ...state, errorReturnConsult: action.payload };
    case "SET_ACCOUNTS_OPTIONS":
      return { ...state, accountsOptions: action.payload };
    case "SET_CREDITS":
      return { ...state, credits: action.payload };
    case "SET_DISPLAY_STATUS":
      return { ...state, displayStatus: action.payload };
    case "SET_NO_CREDIT":
      return { ...state, noCredit: action.payload };
    case "SET_COURT_VALUE":
      return { ...state, courtValue: action.payload };
    case "SET_FETCH":
      return { ...state, fetch: action.payload };
    case "SET_CAN_SHOW":
      return { ...state, canShow: action.payload };
    case "SET_LOADING_ANALYSIS":
      return { ...state, loadingAnalysis: action.payload };
    case "SET_DISPLAY_CRITERIO":
      return { ...state, criterioDisplay: action.payload };
    case "SET_PROCESSES_COUNT":
      return { ...state, processesCount: action.payload };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

export const Consult: React.FC = () => {
  const isMobile = useIsMobile();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(consultationReducer, initialState);
  const {
    cpf,
    isDocValid,
    searching,
    progress,
    analysisResult,
    invalidCpf,
    counter,
    errorReturnConsult,
    accountsOptions,
    credits,
    displayStatus,
    noCredit,
    courtValue,
    fetch,
    canShow,
    criterioDisplay,
    processesCount
  } = state;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [modalTermVisible, setModalTermVisible] = useState<boolean>(false);
  const [modalFreeCredit, setModalFreeCredit] = useState<boolean>(false);

  const limitBarProgress = 90;

  const { selectedAccountCombo, setSelectedAccountCombo } =
    useNavigationContext();
  const { role, scenarioId } = permissionsStore();

  const startTimerCount = () => {
    const consultationState =
      readFromLocalStorage<ConsultationState>("consultationState");
    if (consultationState && consultationState.searching) {
      let startTime = readFromLocalStorage<string>("startTime");
      if (!startTime) {
        startTime = Date.now().toString();
        saveToLocalStorage("startTime", startTime);
      }
      const interval = setInterval(() => {
        const elapsedSeconds = Math.floor(
          (Date.now() - Number(startTime)) / 1000
        );
        dispatch({ type: "SET_COUNTER", payload: elapsedSeconds });
      }, 1000);
      return () => clearInterval(interval);
    }
    removeFromLocalStorage("startTime");
  };

  useEffect(() => {
    startTimerCount();
  }, []);

  const normalizeScenarioId = () => {
    if (!Array.isArray(scenarioId)) {
      return [{ scenarioId, accountId: selectedAccountCombo }];
    }
    return scenarioId;
  };

  const getCriterioId = () => {
    const normalizedScenario = normalizeScenarioId();
    return normalizedScenario.find(
      (scenario) => scenario.accountId === selectedAccountCombo
    )?.scenarioId;
  };

  const handleAccept = async () => {
    setModalTermVisible(false);
    await HomeService.setTermsConditions();
    await EmbaixadorService.setEmbaixador();
    await EmbaixadorService.setTermsConditions();
  };
  const fetchProcessCount = async (accountId: number) => {
    const response = await HomeService.getProcessCount(accountId);
    if (!response.success) {
      return;
    }
    dispatch({ type: "SET_PROCESSES_COUNT", payload: response.count ?? 0 });
  };

  useEffect(() => {
    if (selectedAccountCombo) {
      fetchProcessCount(selectedAccountCombo);
    }
  }, [selectedAccountCombo]);

  useEffect(() => {
    async function fetchScenario() {
      const criterioId = getCriterioId();
      if (!criterioId) {
        dispatch({ type: "SET_DISPLAY_STATUS", payload: false });
        dispatch({ type: "SET_DISPLAY_CRITERIO", payload: true });
        console.warn("CriterioId não encontrado");
        return;
      } else {
        const { success, message, data } = await ScenariosService.getScenario(
          selectedAccountCombo!,
          criterioId
        );
        if (!success) {
          console.error("Erro ao buscar cenário:", message);
          return;
        }
        //eslint-disable-next-line
        //@ts-ignore
        if (data && data.prompt) {
          dispatch({ type: "SET_DISPLAY_STATUS", payload: true });
          dispatch({ type: "SET_DISPLAY_CRITERIO", payload: false });
        } else {
          dispatch({ type: "SET_DISPLAY_STATUS", payload: false });
          dispatch({ type: "SET_DISPLAY_CRITERIO", payload: true });
        }
      }
    }

    fetchScenario();
  }, [scenarioId, selectedAccountCombo]);

  // Limpa os itens temporários do localStorage
  const clearTemporaryStorage = () => {
    removeFromLocalStorage("consultationState");
    removeFromLocalStorage("startTime");
  };

  // Ao montar, verifica se há resultado salvo no localStorage
  useEffect(() => {
    const storedResult =
      readFromLocalStorage<TAnalysisResults>("analysisResult");
    if (storedResult) {
      clearTemporaryStorage();
      dispatch({ type: "SET_ANALYSIS_RESULT", payload: storedResult });
      dispatch({ type: "SET_CAN_SHOW", payload: true });
      dispatch({
        type: "SET_ERROR_RETURN_CONSULT",
        payload: storedResult?.errorReturnConsult
      });
    }
  }, []);

  useEffect(() => {
    if (analysisResult) {
      clearTemporaryStorage();
      saveToLocalStorage("analysisResult", analysisResult);
      dispatch({ type: "SET_CAN_SHOW", payload: true });
    }
  }, [analysisResult]);

  useEffect(() => {
    const handleConnect = () => {
      const consultationState = localStorage.getItem("consultationState");
      if (consultationState) {
        try {
          const state = JSON.parse(consultationState);

          let startTime = readFromLocalStorage<string>("startTime");
          if (!startTime) {
            startTime = Date.now().toString();
          }

          if (state.searching === true) {
            socket.emit("register", {
              accountId: selectedAccountCombo,
              cpf: cpf.replace(/[^a-zA-Z0-9]/g, ""),
              startTime
            });
          }
        } catch (error) {
          console.error("Erro ao parsear consultationState", error);
        }
      }
    };

    socket.on("connect", handleConnect);

    if (socket.connected) {
      handleConnect();
    }
  }, [selectedAccountCombo]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const consultationState = localStorage.getItem("consultationState");
        if (consultationState) {
          const startTime =
            readFromLocalStorage<string>("startTime") || Date.now().toString();
          socket.emit("register", {
            accountId: selectedAccountCombo,
            cpf: cpf.replace(/[^a-zA-Z0-9]/g, ""),
            startTime
          });
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [selectedAccountCombo, cpf]);

  const updateCounter = async () => {
    if (!selectedAccountCombo) return;
    try {
      const accounts = (await fetchAccounts()) ?? [];
      const credits =
        accounts.find(({ id }) => id === selectedAccountCombo)?.credits ?? 0;
      dispatch({ type: "SET_CREDITS", payload: credits });
      await fetchProcessCount(selectedAccountCombo);
    } catch (error) {
      console.error("Erro ao atualizar contador:", error);
    }
  };

  useEffect(() => {
    socket.on("analyzeResult", async (data) => {
      const parsedData = JSON.parse(data.message) as TAnalysisResults;
      await updateCounter();
      try {
        removeFromLocalStorage("consultationState");
        removeFromLocalStorage("startTime");
        saveToLocalStorage("analysisResult", parsedData);
        dispatch({ type: "SET_ANALYSIS_RESULT", payload: parsedData });
        dispatch({ type: "SET_LOADING_ANALYSIS", payload: false });
        dispatch({ type: "SET_CAN_SHOW", payload: true });
        dispatch({ type: "SET_SEARCHING", payload: false });
      } catch (error) {
        console.error("Erro:", error);
      }
    });
  }, []);

  const fetchAccounts = async () => {
    const allAccounts: TAccount[] = [];
    let page = 1;
    let total = 0;
    try {
      do {
        const response: TAccountRes | null = await AccountsService.getAccounts(
          page,
          "",
          100
        );
        if (response && response.data && Array.isArray(response.data)) {
          allAccounts.push(...response.data);
        }
        total = response && response.meta ? response.meta.total : 0;
        page++;
      } while (allAccounts.length < total);
      return allAccounts;
    } catch (error) {
      console.error("Erro ao buscar contas:", error);
    }
  };

  const setupAccountData = async () => {
    const allAccounts: TAccount[] = [];
    try {
      const accounts = await fetchAccounts();
      if (accounts) {
        allAccounts.push(...accounts);
      }
      if (allAccounts.length) {
        if (selectedAccountCombo) {
          const selected = allAccounts.find(
            (acc) => acc.id === selectedAccountCombo
          );
          setSelectedAccountCombo(selected!.id);
          dispatch({ type: "SET_CREDITS", payload: selected!.credits });
        } else {
          const oldestAccount = allAccounts.reduce((oldest, current) =>
            new Date(current.createdAt) < new Date(oldest.createdAt)
              ? current
              : oldest
          );
          setSelectedAccountCombo(oldestAccount.id);
          dispatch({ type: "SET_CREDITS", payload: oldestAccount.credits });
        }
        const options = allAccounts.map((acc) => ({
          value: acc.id.toString(),
          label: acc.name,
          payload: acc.credits
        }));
        dispatch({ type: "SET_ACCOUNTS_OPTIONS", payload: options });
      }
    } catch (error) {
      console.error("Erro ao buscar contas:", error);
      throw error;
    }
  };

  useEffect(() => {
    setupAccountData();
    /* eslint-disable-next-line */
  }, [user, fetch]);

  useEffect(() => {
    dispatch({ type: "SET_COUNTER", payload: 0 });
    const timer = setInterval(() => {
      dispatch({ type: "INCREMENT_COUNTER" });
    }, 1000);
    return () => clearInterval(timer);
  }, [searching]);

  const minutes = Math.floor(counter / 60);
  const seconds = counter % 60;

  // Função para salvar o estado da consulta no localStorage
  interface ConsultationStorageState {
    cpf: string;
    isDocValid: boolean;
    searching: boolean;
    progress: number;
  }
  const saveConsultationState = (stateToSave: ConsultationStorageState) => {
    saveToLocalStorage("consultationState", {
      ...stateToSave,
      searching: true
    });
  };

  const handleConsult = () => {
    dispatch({ type: "SET_INVALID_CPF", payload: false });
    dispatch({ type: "SET_SEARCHING", payload: true });
    dispatch({ type: "SET_ANALYSIS_RESULT", payload: null });
    dispatch({ type: "SET_PROGRESS", payload: 0 });
    dispatch({
      type: "SET_ERROR_RETURN_CONSULT",
      payload: false
    });

    removeFromLocalStorage("analysisResult");
    removeFromLocalStorage("consultationState");
    removeFromLocalStorage("startTime");

    const startTime = Date.now().toString();
    saveToLocalStorage("startTime", startTime);

    const initialState: ConsultationStorageState = {
      cpf,
      isDocValid,
      searching: true,
      progress: 0
    };
    saveConsultationState(initialState);

    if (isDocValid && cpf) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        const storedState =
          readFromLocalStorage<ConsultationStorageState>("consultationState");
        if (storedState) {
          const randomIncrement = Math.floor(Math.random() * 5);
          const newProgress = Math.min(
            storedState.progress + randomIncrement,
            limitBarProgress
          );
          saveConsultationState({ ...storedState, progress: newProgress });
          dispatch({ type: "SET_PROGRESS", payload: newProgress });
          if (newProgress >= 89 && intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, 600);
      analyzeCPF();
    } else {
      dispatch({ type: "SET_INVALID_CPF", payload: true });
      dispatch({ type: "SET_CPF", payload: "" });
      dispatch({ type: "SET_SEARCHING", payload: false });
      removeFromLocalStorage("consultationState");
      removeFromLocalStorage("startTime");
    }
  };

  // Verifica no localStorage se há uma consulta em andamento
  useEffect(() => {
    const storedState =
      readFromLocalStorage<ConsultationState>("consultationState");
    if (storedState && storedState.searching) {
      dispatch({ type: "SET_CPF", payload: storedState.cpf || "" });
      dispatch({ type: "SET_PROGRESS", payload: storedState.progress || 0 });
      dispatch({ type: "SET_SEARCHING", payload: true });
      if (!intervalRef.current && storedState.progress < 89) {
        intervalRef.current = setInterval(() => {
          // Releitura do estado armazenado para garantir que o progresso seja atualizado corretamente
          const currentState =
            readFromLocalStorage<ConsultationState>("consultationState");
          if (!currentState) return;
          const randomIncrement = Math.floor(Math.random() * 5);
          const newProgress = Math.min(
            currentState.progress + randomIncrement,
            89
          );
          saveConsultationState({ ...currentState, progress: newProgress });
          dispatch({ type: "SET_PROGRESS", payload: newProgress });
          if (newProgress >= limitBarProgress && intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }, 600);
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (localStorage.getItem("puxa-capivara-free-credit")) {
      setModalFreeCredit(true);
    }
  }, []);

  const analyzeCPF = async () => {
    try {
      const response = await HomeService.analyze(
        cpf,
        selectedAccountCombo!,
        user!.id
      );

      if (response.success) {
        dispatch({ type: "SET_LOADING_ANALYSIS", payload: true });
        dispatch({ type: "SET_ANALYSIS_RESULT", payload: null });
        toast.info(response.message);
      } else {
        toast.error(response.message);
        dispatch({ type: "SET_SEARCHING", payload: false });
        removeFromLocalStorage("startTime");
        removeFromLocalStorage("consultationState");
        if (
          response.message ===
          "A conta selecionada não possui créditos suficientes!"
        ) {
          dispatch({ type: "SET_NO_CREDIT", payload: true });
          return;
        }
        if (response.message === "Critério não existe!") {
          navigate("/scenarios");
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "SET_PROGRESS", payload: 0 });
      dispatch({ type: "SET_FETCH", payload: true });
      dispatch({ type: "SET_LOADING_ANALYSIS", payload: false });
    }
  };

  const parseArticle = (param?: string) => (param ? param : "N/A");

  const convertDate = (distributionDate: string) => {
    const date = distributionDate ? new Date(distributionDate) : new Date();
    return date.toLocaleDateString("pt-BR", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric"
    });
  };

  const renderProcess = () => {
    if (!analysisResult?.processes?.length) return null;
    return (
      <ProcessList>
        <span className="label">Processos:</span>
        {analysisResult.processes.map((process, index) => (
          <ProcessItem key={index}>
            <span className="lbl">Data de distribuição:</span>
            <span>
              {process.distributionDate !== "-"
                ? convertDate(process.distributionDate)
                : "-"}
            </span>
            {process.processUniqueNumber !== "-" ? (
              parseStatus(process.status)
            ) : (
              <span>Processo Inválido</span>
            )}
            <span className="lbl">Número único de processo:</span>
            <a href={process.processURL} target="_blank">
              {process.processUniqueNumber}
            </a>
            <span className="lbl">Situação do processo:</span>
            <span>{process.processStep}</span>
            <span className="lbl">Polo:</span>
            <span>{process.polo}</span>
            <span className="lbl">Esfera:</span>
            <span>{process.esfera.toUpperCase()}</span>
            <span className="lbl">Artigo:</span>
            <span>{parseArticle(process.articleNumber)}</span>
            <span className="lbl">Detalhes:</span>
            <p style={{ marginTop: 5, textAlign: "justify" }}>
              {process.details}
            </p>
            <Line />
          </ProcessItem>
        ))}
      </ProcessList>
    );
  };

  const formatText = (input: string) => {
    return input.split(/\n\s*\n/).map((paragraph, pIndex) => {
      const trimmed = paragraph.trim();

      const titleMatch = trimmed.match(/^\*\*(.+)\*\*$/);
      if (titleMatch) {
        return (
          <strong
            key={pIndex}
            style={{
              display: "block",
              margin: "1em 0",
              fontFamily: "sans-serif",
              fontSize: "1.3em"
            }}
          >
            {titleMatch[1]}
          </strong>
        );
      }

      return (
        <p key={pIndex} className="details">
          {paragraph.split(/(\*\*[^*]+\*\*)/g).flatMap((chunk, cIndex) => {
            const boldMatch = chunk.match(/^\*\*(.+)\*\*$/);
            if (boldMatch) {
              return (
                <strong key={`b-${pIndex}-${cIndex}`}>{boldMatch[1]}</strong>
              );
            }
            return chunk.split("\n").map((line, lIndex) =>
              lIndex > 0 ? (
                <React.Fragment key={`br-${pIndex}-${cIndex}-${lIndex}`}>
                  <br />
                  {line}
                </React.Fragment>
              ) : (
                <span key={`t-${pIndex}-${cIndex}`}>{line}</span>
              )
            );
          })}
        </p>
      );
    });
  };

  const parseCredits = (id: string) => {
    const account = accountsOptions.find((acc) => acc.value === id);
    dispatch({ type: "SET_CREDITS", payload: account?.payload || 0 });
  };

  const parseStatus = (status: boolean) => {
    if (!displayStatus) return null;
    return (
      <>
        <span className="lbl">Status:</span>
        {status ? (
          <span className="detail">&#x2705; Aprovado</span>
        ) : (
          <span className="detail">&#x274C; Reprovado</span>
        )}
      </>
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({
        type: "SET_COURT_VALUE",
        payload: Math.floor(Math.random() * 90)
      });
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const displayConsultedName = (consultedName: string): string => {
    if (consultedName.length < 11) return consultedName;
    const last11 = consultedName.slice(-11);
    const isNumeric = /^\d{11}$/.test(last11);
    if (isNumeric) {
      const formattedCpf = cpfValidator.format(last11);
      const beginning = consultedName.slice(0, consultedName.length - 11);
      return beginning + formattedCpf;
    } else {
      return consultedName;
    }
  };
  useEffect(() => {
    const fetchDisplayTerms = async () => {
      await HomeService.getTermsConditions().then(async (data) => {
        const { termsConditions } = data;
        setModalTermVisible(!termsConditions);
      });
    };
    fetchDisplayTerms();
  }, []);

  const toggleModalFreeCredits = () => {
    setModalFreeCredit((prev) => !prev);
  };

  return (
    <Page.Root>
      {!isMobile && (
        <Page.Header
          title="Consultar"
          key="consult"
          iconPage={<ContentPasteSearchOutlinedIcon />}
          controlElement={[
            ["owner", "support"].includes(role) && (
              <AccountSelectContainer key="account-select">
                <span className="label">Conta selecionada:</span>
                <Input.Select
                  onChange={(newValue) => {
                    setSelectedAccountCombo(Number(newValue));
                    parseCredits(newValue.toString());
                  }}
                  selectedValue={selectedAccountCombo?.toString()}
                  options={accountsOptions}
                />
              </AccountSelectContainer>
            ),
            <div
              style={{
                width: "inherit",
                display: "flex",
                alignItems: "end"
              }}
            >
              <ConsultLabel qty={processesCount}>
                Consultas realizadas: ({processesCount})
              </ConsultLabel>
            </div>,
            <div
              style={{
                width: "inherit",
                display: "flex",
                flexDirection: "column",
                alignItems: "end"
              }}
            >
              <ConsultLabel qty={credits}>
                Consultas restantes: ({credits})
              </ConsultLabel>
              {credits === 0 && (
                <ConsultLabelWarning onClick={() => navigate("/credits")}>
                  Recarregue seus créditos
                </ConsultLabelWarning>
              )}
            </div>
          ].filter(Boolean)}
        ></Page.Header>
      )}
      <MobileContainer>
        {role === "owner" && user?.userType === "COMPANY" && (
          <AccountSelectContainer key="account-select">
            <span className="label">Conta selecionada:</span>
            <Input.Select
              onChange={(newValue) => {
                setSelectedAccountCombo(Number(newValue));
                parseCredits(newValue.toString());
              }}
              selectedValue={selectedAccountCombo?.toString()}
              options={accountsOptions}
            />
          </AccountSelectContainer>
        )}
      </MobileContainer>

      <ConsultMetadataContainer>
        <div
          style={{
            width: "inherit",
            display: "flex",
            alignItems: "end"
          }}
        >
          <ConsultLabelMobile>
            Consultas realizadas: ({processesCount})
          </ConsultLabelMobile>
        </div>

        {user?.paymentType === "PREPAID" && (
          <div
            style={{
              width: "inherit",
              display: "flex",
              flexDirection: "column",
              alignItems: "end"
            }}
          >
            <ConsultLabelMobile>
              Consultas restantes: ({credits})
            </ConsultLabelMobile>
            {credits === 0 && (
              <ConsultLabelWarning onClick={() => navigate("/credits")}>
                Recarregue seus créditos
              </ConsultLabelWarning>
            )}
          </div>
        )}
      </ConsultMetadataContainer>
      <ContainerData>
        {!noCredit && (
          <FormContainer className="relative !w-5/6">
            <Input.Masked
              label="CPF da pessoa a ser analisada:"
              type="CPF"
              setValue={(value) =>
                dispatch({ type: "SET_CPF", payload: value })
              }
              setIsValid={(isValid) =>
                dispatch({ type: "SET_IS_DOC_VALID", payload: isValid })
              }
              isValid={isDocValid}
              required
            />
            {searching && (
              <span className="absolute right-0 mt-1 mr-1 info animate-pulse">
                {String(minutes).padStart(2, "0")}:
                {String(seconds).padStart(2, "0")}
              </span>
            )}
            <Page.Button
              disabled={searching}
              onClick={handleConsult}
              label={searching ? "Processando..." : "Analisar"}
              color="white"
              borderColor={theme.colors.palete.gold}
            />
            {invalidCpf && (
              <div className="w-full flex items-center justify-center rounded h-10 text-black">
                <AlertTriangle className="mr-2 text-red-600" />
                <strong className="text-red-600">Insira um CPF válido</strong>
              </div>
            )}

            {searching && (
              <DivSearching className="relative">
                <Progress value={progress} />
                <span className="info animate-pulse">{court[courtValue]}</span>
              </DivSearching>
            )}
          </FormContainer>
        )}
        {noCredit && (
          <NoCreditContainer>
            <OctagonAlert size={70} />
            <span className="info">
              Você não possui créditos para realizar consultas.
            </span>
            <div onClick={() => navigate("/credits")}>
              <p className="p-2">ADICIONAR CRÉDITOS</p>
            </div>
          </NoCreditContainer>
        )}
        {!displayStatus && criterioDisplay && (
          <ContainerData className="flex items-center text-center justify-center p-4">
            <InfoOutlinedIcon style={{ fontSize: 50 }} />
            <InfoText>
              Por padrão, a aprovação e reprovação estão desativadas. Você pode
              configurar{" "}
              <a
                href="/scenarios"
                style={{
                  fontFamily: "sans-serif",
                  fontWeight: "800"
                }}
              >
                agora
              </a>{" "}
              ou posteriormente no ambiente de critérios, acessando o menu do
              aplicativo.
            </InfoText>
          </ContainerData>
        )}
      </ContainerData>
      <FreeCredit
        modal={modalFreeCredit}
        toggleModal={toggleModalFreeCredits}
      />
      {modalTermVisible && (
        <TermsModal onAccept={handleAccept} pdfUrl="/termo-utilizacao.pdf" />
      )}

      {errorReturnConsult && (
        <ContainerData className="flex items-center text-center justify-center p-4">
          <InfoOutlinedIcon className="mr-2" />
          <strong>Os processos foram anexados no PDF!</strong>
        </ContainerData>
      )}

      {canShow && analysisResult && (
        <ContainerData>
          <ResponseContainer>
            {displayStatus && (
              <>
                <span className="label">Status:</span>
                {analysisResult.status ? (
                  <span className="info">&#x2705; Aprovado</span>
                ) : (
                  <span className="info">&#x274C; Reprovado</span>
                )}
              </>
            )}
            <span className="info">
              Linhas analisadas no processo:{" "}
              {analysisResult.processesLinesCount}
            </span>
            <span className="info">Data da consulta: {convertDate("")}</span>
            <span className="info">
              Consultado: {" " + displayConsultedName(analysisResult.name)}
            </span>
            <span className="info">
              Quantidade de processos: {analysisResult.processesCount}
            </span>
          </ResponseContainer>
          <DetailsContainer>
            <span className="label">Detalhes:</span>
            {formatText(analysisResult.summary)}
            <div
              style={{ width: isMobile ? "70vw" : "22vw", marginTop: "3vh" }}
            >
              <DownloadPdf href={analysisResult?.fileUrl} target="_blank">
                Baixar PDF
              </DownloadPdf>
            </div>
          </DetailsContainer>
        </ContainerData>
      )}

      <ContainerData>{renderProcess()}</ContainerData>
    </Page.Root>
  );
};
